import React, { Suspense, type ComponentType } from 'react';
import { Spin } from 'antd';
import { css } from '@shared/utils/omitUndefined';
import styles from './lazyRoute.module.css';

const RouteLoadingFallback: React.FC = () => (
  <div
    role="status"
    aria-busy="true"
    aria-label="불러오는 중"
    className={css(styles.s0_displayjustifyCalignIteminHeighpadding)}
  >
    <Spin size="large" aria-label="불러오는 중" />
  </div>
);

export function lazyNamed<P extends object>(
  importFn: () => Promise<Record<string, ComponentType<P>>>,
  exportName: string
): React.FC<P> {
  const LazyComponent = React.lazy(() =>
    importFn().then((mod) => {
      const Component = mod[exportName];
      if (!Component) {
        throw new Error(`lazyNamed: export "${exportName}" not found`);
      }
      return { default: Component };
    })
  );

  const Wrapped = (props: P) => {
    const Component = LazyComponent as unknown as ComponentType<P>;
    return (
      <Suspense fallback={<RouteLoadingFallback />}>
        <Component {...props} />
      </Suspense>
    );
  };

  return Wrapped as React.FC<P>;
}

export function lazyDefault<P extends object>(
  importFn: () => Promise<{ default: ComponentType<P> }>
): React.FC<P> {
  const LazyComponent = React.lazy(importFn);

  const Wrapped = (props: P) => {
    const Component = LazyComponent as unknown as ComponentType<P>;
    return (
      <Suspense fallback={<RouteLoadingFallback />}>
        <Component {...props} />
      </Suspense>
    );
  };

  return Wrapped as React.FC<P>;
}
